import { useMemo, useState } from "react";
import { Badge, Button, Card, Collapse } from "react-bootstrap";
import {
  FaBuilding,
  FaChevronDown,
  FaCircleInfo,
  FaGlobe,
  FaPhone,
  FaShieldHalved,
  FaTriangleExclamation,
  FaWhatsapp,
} from "react-icons/fa6";
import "./DetailContactCard.css";
import {
  buildLocationText,
  getApprovalLabel,
  getConditionLabel,
  getFurnishingLabel,
  getInitial,
  getListingSourceLabel,
  getOrientationLabel,
  sanitizePhone,
} from "./detailProperty.helpers";

const DetailContactCard = ({ property }) => {
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  const contact = useMemo(() => {
    const marketing = property?.marketing;
    const owner = property?.user;

    if (marketing) {
      return {
        name: marketing.name,
        role: "Marketing Property",
        phone: marketing.phone_number || marketing.whatsApp_no || "",
        whatsapp: marketing.whatsApp_no || marketing.phone_number || "",
        avatar: marketing.photo_profile || "",
        address: marketing.address || "",
        developer: marketing.developer || null,
      };
    }

    return {
      name: owner?.name || "Pemilik Properti",
      role: "Owner",
      phone: owner?.phone_number || "",
      whatsapp: owner?.phone_number || "",
      avatar: "",
      address: property?.address || "",
      developer: null,
    };
  }, [property]);

  const phoneNumber = sanitizePhone(contact.phone);
  const whatsappNumber = sanitizePhone(contact.whatsapp);

  const phoneHref = phoneNumber ? `tel:${phoneNumber}` : "#";
  const whatsappHref = whatsappNumber ? `https://wa.me/${whatsappNumber}` : "#";

  const locationText = buildLocationText(property);

  return (
    <div className="detail-contact-stack">

      <Card className="detail-contact-card-v2 border-0">
        <Card.Body>
          <div className="detail-contact-top">
            <div className="detail-card-title-wrap">
              <FaCircleInfo className="detail-card-title-icon" />
              <span>Informasi Kontak</span>
            </div>
          </div>
          <div className="detail-contact-header">
            <div className={`detail-contact-avatar ${contact.avatar ? "has-image" : "has-initial"}`}>
              {contact.avatar ? (
                <img src={contact.avatar} alt={contact.name} />
              ) : (
                <span>{getInitial(contact.name)}</span>
              )}
            </div>

            <div className="detail-contact-meta">
              <div className="detail-contact-name">{contact.name}</div>
              <div className="detail-contact-role">{contact.role}</div>
              {contact.address ? (
                <div className="detail-contact-subtitle">{contact.address}</div>
              ) : null}
            </div>
          </div>
          {contact.developer ? (
            <div className="developer-card">
              <div className="developer-card-header">
                <FaBuilding />
                <span>Developer Property</span>
              </div>

              <div className="developer-card-body">
                {contact.developer.logo_url ? (
                  <img
                    src={contact.developer.logo_url}
                    alt={contact.developer.name}
                    className="developer-logo"
                  />
                ) : (
                  <div className="developer-logo developer-logo--placeholder">
                    <FaBuilding />
                  </div>
                )}

                <div className="developer-card-text">
                  <div className="developer-name">{contact.developer.name}</div>
                  <div className="developer-location">
                    {[contact.developer.city, contact.developer.province]
                      .filter(Boolean)
                      .join(", ") || "-"}
                  </div>
                  {contact.developer.website ? (
                    <button
                      type="button"
                      className="developer-website-link"
                      onClick={() =>
                        window.open(contact.developer.website, "_blank", "noreferrer")
                      }
                    >
                      <FaGlobe className="me-1" />
                      Kunjungi Website
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}

          <div className="detail-contact-summary">
            <Badge bg="light" text="dark" className="detail-summary-badge">
              {property?.property_type?.name || "-"}
            </Badge>
            <Badge bg="light" text="dark" className="detail-summary-badge">
              {property?.certificate_type?.name || "-"}
            </Badge>
            <Badge bg="light" text="dark" className="detail-summary-badge">
              {property?.status_label || "-"}
            </Badge>
            <Badge bg="light" text="dark" className="detail-summary-badge">
              {property?.approval_label || getApprovalLabel(property?.approval_status)}
            </Badge>
          </div>

          {/* Info List */}
          <div className="detail-contact-info-list">
            <div className="detail-contact-info-row">
              <span>Lokasi</span>
              <strong>{locationText || "-"}</strong>
            </div>
            <div className="detail-contact-info-row">
              <span>Listing Source</span>
              <strong>{getListingSourceLabel(property?.listing_source)}</strong>
            </div>
            <div className="detail-contact-info-row">
              <span>Orientasi</span>
              <strong>{getOrientationLabel(property?.building_orientation)}</strong>
            </div>
            <div className="detail-contact-info-row">
              <span>Kondisi</span>
              <strong>{getConditionLabel(property?.building_condition)}</strong>
            </div>
            <div className="detail-contact-info-row">
              <span>Perabotan</span>
              <strong>{getFurnishingLabel(property?.furnishing_status)}</strong>
            </div>
          </div>

          <div className="detail-contact-actions">
            <Button
              href={phoneHref}
              className="detail-contact-btn detail-contact-btn-call"
              disabled={!phoneNumber}
            >
              <FaPhone />
              Telepon
            </Button>

            <Button
              href={whatsappHref}
              target="_blank"
              rel="noreferrer"
              className="detail-contact-btn detail-contact-btn-wa"
              disabled={!whatsappNumber}
            >
              <FaWhatsapp />
              WhatsApp
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Card className="detail-disclaimer-card border-0">
        <Card.Body>
          <button
            type="button"
            className={`detail-disclaimer-toggle detail-disclaimer-toggle--full ${
              showDisclaimer ? "active" : ""
            }`}
            onClick={() => setShowDisclaimer((prev) => !prev)}
            aria-expanded={showDisclaimer}
            aria-controls="detail-disclaimer-panel"
          >
            <span>Disclaimer & Informasi</span>
            <FaChevronDown />
          </button>

          <Collapse in={showDisclaimer}>
            <div id="detail-disclaimer-panel" className="detail-disclaimer-panel">
              <div className="detail-disclaimer-section">
                <div className="detail-disclaimer-head">
                  <FaShieldHalved className="detail-disclaimer-icon" />
                  <div>
                    <div className="detail-disclaimer-title">Kebijakan Urbania</div>
                    <div className="detail-disclaimer-subtitle">
                      Ketentuan penggunaan kontak dan privasi
                    </div>
                  </div>
                </div>
                <div className="detail-disclaimer-text">
                  Dengan klik tombol kontak, Pengguna setuju dengan Syarat Pengguna dan
                  Kebijakan Privasi.
                </div>
              </div>

              <div className="detail-disclaimer-section">
                <div className="detail-disclaimer-head">
                  <FaTriangleExclamation className="detail-disclaimer-icon" />
                  <div>
                    <div className="detail-disclaimer-title">Informasi Properti</div>
                    <div className="detail-disclaimer-subtitle">
                      Verifikasi sebelum keputusan
                    </div>
                  </div>
                </div>
                <div className="detail-disclaimer-text">
                  Informasi properti yang disajikan sepenuhnya merupakan tanggung jawab
                  pengiklan. Pengguna disarankan untuk melakukan verifikasi dengan pengiklan
                  sebelum mengambil keputusan.
                </div>
              </div>

              <div className="detail-disclaimer-section">
                <div className="detail-disclaimer-head">
                  <FaCircleInfo className="detail-disclaimer-icon" />
                  <div>
                    <div className="detail-disclaimer-title">
                      Ada masalah dengan properti atau agen terkait?
                    </div>
                    <div className="detail-disclaimer-subtitle">
                      Laporkan bila ada kendala
                    </div>
                  </div>
                </div>

                <button type="button" className="detail-report-btn">
                  Beri Tahu Kami
                </button>
              </div>
            </div>
          </Collapse>
        </Card.Body>
      </Card>
    </div>
  );
};

export default DetailContactCard;
